import { type ReactNode, useMemo, useState } from 'react';
import { DownloadOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Switch, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoaderEcho from '@/components/LoaderEcho';
import ModalEcho from '@/components/modals/Modal/ModalEcho';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import { useDerivedNetworkContext } from '@/context';
import LocationMapEcho from '@/routes/AudiencePage/charts/MapChart/LocationMapEcho';
import {
  getCountryFlagEmoji,
  getCountryName,
  hasCountryFlag,
} from '@/routes/AudiencePage/charts/MapChart/countryUtils';
import { useLocationPlaysQuery } from '@/services/revenueApi';
import type {
  DateRange,
  DownloadConfig,
  LocationData,
  LocationPlaysResponse,
  ShowFilter,
  TableData,
} from '@/types';
import { Platform } from '@/types';
import { isSocialPlatform } from '@/types/api/platforms';
import {
  trackAudienceLocationModalDownloadClicked,
  trackAudienceLocationModalHideSocialsToggled,
  trackAudienceLocationModalOpened,
} from './audienceModalMixpanel';
import useLocationCsvExport from './useLocationCsvExport';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';

type LocationDetailsRow = {
  key: string;
  country: string;
  [platformMetric: string]: string | number | null;
};

type LocationDetailsColumn = {
  key: string;
  header: ReactNode;
  csvHeader?: string;
  align?: 'left' | 'right';
  className?: string;
  sortable?: boolean;
  type?: 'number' | 'percentage';
  render: (row: LocationDetailsRow) => ReactNode;
};

const mapLegendItems = [
  { label: '>20%', color: '#5a97e3' },
  { label: '>0-20%', color: '#a9caf1' },
  { label: 'No data', color: '#e3e4e7' },
];

const DEFAULT_PAGE_SIZE = 10;
const TOTAL_PERCENTAGE_KEY = 'totalPercentage';
const MAP_MAX_WIDTH = 768;

const isPodcastPlayerPlatform = (platform: string): boolean =>
  platform === Platform.Acast || platform === 'Podcast Players';

const hasSocialPlatformsInPlays = (plays: LocationPlaysResponse['plays']): boolean =>
  plays.some(({ platform }) => isSocialPlatform(platform));

const getPlatformAudienceLabel = (platform: string): string =>
  isPodcastPlayerPlatform(platform) ? 'Podcast Players' : platform;

const getPlatformPercentageLabel = (platform: string): string =>
  isPodcastPlayerPlatform(platform)
    ? '% of total podcast audience'
    : `% of total ${platform} audience`;

const formatPercentage = (value: string | number | null): string =>
  value != null ? `${value}%` : '—';

const formatPlatformValue = (value: string | number | null): ReactNode => value ?? '—';

const buildLocationDetailsTableData = (
  plays: LocationPlaysResponse['plays'],
  countryOrder: string[],
  totalCountryDistribution: LocationData[]
): LocationDetailsRow[] => {
  const countryMap: Record<string, LocationDetailsRow> = {};

  totalCountryDistribution.forEach(({ label, percentage }) => {
    countryMap[label] = {
      key: label,
      country: getCountryName(label),
      [TOTAL_PERCENTAGE_KEY]: percentage,
    };
  });

  plays.forEach(({ platform, country_distribution }) => {
    country_distribution.forEach(({ label, value, percentage }) => {
      if (!countryMap[label]) {
        countryMap[label] = {
          key: label,
          country: getCountryName(label),
        };
      }

      countryMap[label][`${platform}Value`] = value;
      countryMap[label][`${platform}Percentage`] = percentage;
    });
  });

  return countryOrder
    .map((countryLabel) => countryMap[countryLabel])
    .filter((row): row is LocationDetailsRow => row != null);
};

const buildLocationDetailsTableColumns = (
  plays: LocationPlaysResponse['plays'],
  hideSocials: boolean,
  hasTotalCountryDistribution: boolean,
  hasSocialPlatforms: boolean
): LocationDetailsColumn[] => [
  {
    key: 'flag',
    header: '',
    className: 'w-10',
    render: (row) =>
      hasCountryFlag(row.key) ? (
        <span aria-label={`${row.country} flag`} role="img">
          {getCountryFlagEmoji(row.key)}
        </span>
      ) : null,
  },
  {
    key: 'country',
    header: 'Country',
    csvHeader: 'Country',
    render: (row) => row.country,
  },
  ...(!hideSocials && hasTotalCountryDistribution && hasSocialPlatforms
    ? [
        {
          key: TOTAL_PERCENTAGE_KEY,
          header: 'Total %',
          render: (row: LocationDetailsRow) => formatPercentage(row[TOTAL_PERCENTAGE_KEY]),
          align: 'right' as const,
          className: 'px-2 w-24 max-w-24',
          sortable: true,
          type: 'percentage' as const,
        },
      ]
    : []),
  ...plays.flatMap((play) => {
    const { platform } = play;
    const percentageColumn = {
      key: `${platform}Percentage`,
      header: getPlatformPercentageLabel(platform),
      render: (row: LocationDetailsRow) => formatPercentage(row[`${platform}Percentage`]),
      align: 'right' as const,
      className: 'px-2 w-32 max-w-32',
      sortable: true,
      type: 'percentage' as const,
    };

    if (isSocialPlatform(platform)) {
      return hideSocials ? [] : [percentageColumn];
    }

    const valueColumn = {
      key: `${platform}Value`,
      header: getPlatformAudienceLabel(platform),
      render: (row: LocationDetailsRow) => formatPlatformValue(row[`${platform}Value`]),
      align: 'right' as const,
      className: 'px-2',
      sortable: true,
      type: 'number' as const,
    };

    return [valueColumn, percentageColumn];
  }),
];

const buildLocationDetailsDataTableRows = (
  tableData: LocationDetailsRow[],
  columns: LocationDetailsColumn[]
): TableData[] =>
  tableData.map((row) => ({
    id: row.key,
    columns: columns.map((column) => ({
      id: column.key,
      value: column.render(row),
      type: column.type,
    })),
  }));

const buildLocationDetailsEchoColumns = (
  columns: LocationDetailsColumn[]
): ColumnsType<TableData> =>
  columns.map((column) => ({
    key: column.key,
    title: column.header,
    align: column.align,
    sorter: column.sortable ?? false,
    className: column.className,
    render: (_value, record) => {
      const cell = record.columns.find((tableColumn) => tableColumn.id === column.key);
      return cell?.value ?? null;
    },
  }));

const getDefaultLocationDetailsSortKey = (columns: LocationDetailsColumn[]): string | undefined =>
  columns.find((column) => column.sortable)?.key;

const buildPercentageMapData = (countryDistribution: LocationData[]): LocationData[] =>
  countryDistribution.map(({ label, percentage }) => ({
    label,
    value: percentage,
    percentage,
  }));

const buildPodcastPlayerMapData = (plays: LocationPlaysResponse['plays']): LocationData[] =>
  buildPercentageMapData(
    plays.find(({ platform }) => isPodcastPlayerPlatform(platform))?.country_distribution ?? []
  );

const formatMapPercentage = (value: number): string => `${value.toFixed(2)}%`;

const LocationMapLegend = () => {
  const { token } = theme.useToken();

  return (
    <Flex
      wrap
      justify="center"
      gap={token.margin}
      role="list"
      aria-label="Map colour scale"
      style={{ marginTop: token.marginXS }}
    >
      {mapLegendItems.map(({ label, color }) => (
        <Flex key={label} align="center" gap={token.marginXS} role="listitem">
          <span
            aria-hidden
            style={{
              width: token.fontSizeSM,
              height: token.fontSizeSM,
              flexShrink: 0,
              borderRadius: token.borderRadiusSM,
              background: color,
              border: `1px solid ${token.colorBorderSecondary}`,
            }}
          />
          <Typography.Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
            {label}
          </Typography.Text>
        </Flex>
      ))}
    </Flex>
  );
};

const LocationModalContent = ({
  dateRange,
  selectedShow,
  hideSocials,
  onHideSocialsChange,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
  hideSocials: boolean;
  onHideSocialsChange: (hideSocials: boolean) => void;
}) => {
  const { token } = theme.useToken();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { showAudienceSocialViews } = useFlags<{ showAudienceSocialViews?: boolean }>();
  const showSocialViews = showAudienceSocialViews === true;
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { showBanner, hideContent, queryFrom } = useIsBeforeDataAvailability(
    'location',
    dateRange.from,
    dateRange.to
  );

  const { data, isPending } = useLocationPlaysQuery(
    selectedNetwork.id,
    queryFrom,
    dateRange.to,
    hideContent ? { enabled: false } : undefined,
    { showIds, excludeSocials: !showSocialViews }
  );

  const plays = useMemo(() => data?.plays ?? [], [data?.plays]);
  const countryOrder = useMemo(() => data?.country_order ?? [], [data?.country_order]);
  const totalCountryDistribution = useMemo(
    () => data?.total_country_distribution ?? [],
    [data?.total_country_distribution]
  );
  const tableData = useMemo(
    () => buildLocationDetailsTableData(plays, countryOrder, totalCountryDistribution),
    [countryOrder, plays, totalCountryDistribution]
  );
  const hasSocialPlatforms = useMemo(() => hasSocialPlatformsInPlays(plays), [plays]);
  const [countrySearch, setCountrySearch] = useState('');
  const columns = useMemo(
    () =>
      buildLocationDetailsTableColumns(
        plays,
        hideSocials,
        totalCountryDistribution.length > 0,
        hasSocialPlatforms
      ),
    [hasSocialPlatforms, hideSocials, plays, totalCountryDistribution.length]
  );
  const defaultSortKey = useMemo(() => getDefaultLocationDetailsSortKey(columns), [columns]);
  const echoColumns = useMemo(() => buildLocationDetailsEchoColumns(columns), [columns]);
  const filteredTableData = useMemo(() => {
    const normalizedSearch = countrySearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return tableData;
    }

    return tableData.filter((row) => row.country.toLowerCase().includes(normalizedSearch));
  }, [countrySearch, tableData]);
  const dataTableRows = useMemo(
    () => buildLocationDetailsDataTableRows(filteredTableData, columns),
    [columns, filteredTableData]
  );
  const mapData = useMemo(
    () =>
      hideSocials
        ? buildPodcastPlayerMapData(plays)
        : buildPercentageMapData(totalCountryDistribution),
    [hideSocials, plays, totalCountryDistribution]
  );

  if (hideContent) {
    return <DataAvailabilityBanner dataType="location" />;
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" style={{ height: '60vh' }}>
        <LoaderEcho data-testid="loader" />
      </Flex>
    );
  }

  if (!data || data.plays.length < 1) {
    return (
      <>
        {showBanner && <DataAvailabilityBanner dataType="location" />}
        <NoDataMessageEcho dataType="location data" />
      </>
    );
  }

  return (
    <Flex vertical gap={token.marginLG} style={{ padding: token.padding }}>
      {showBanner && <DataAvailabilityBanner dataType="location" />}
      <Flex justify="center">
        <Flex vertical style={{ width: '100%', maxWidth: MAP_MAX_WIDTH }}>
          <LocationMapEcho
            platformData={mapData}
            valueFormatter={formatMapPercentage}
            valueScale="percentage"
          />
          <LocationMapLegend />
        </Flex>
      </Flex>
      <Flex align="center" gap={token.margin} justify="space-between">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search country"
          aria-label="Search country"
          value={countrySearch}
          onChange={(event) => {
            setCountrySearch(event.target.value);
          }}
        />
        {hasSocialPlatforms && (
          <Flex align="center" gap={token.marginXS} flex="none">
            <Typography.Text>Hide socials</Typography.Text>
            <Switch
              id="toggle-hide-location-socials"
              checked={hideSocials}
              onChange={(nextHideSocials) => {
                onHideSocialsChange(nextHideSocials);
                trackAudienceLocationModalHideSocialsToggled(nextHideSocials);
              }}
            />
          </Flex>
        )}
      </Flex>
      <DataTableEcho
        key={defaultSortKey}
        columns={echoColumns}
        dataSource={dataTableRows}
        rowKey="id"
        initialState={
          defaultSortKey ? { sorting: { key: defaultSortKey, direction: 'desc' } } : undefined
        }
        pagination={{
          summary: (fromPage, toPage, total) => `Showing ${fromPage}-${toPage} of ${total}`,
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: [10, 25, 50],
        }}
      />
    </Flex>
  );
};

const LocationModalEcho = () => {
  const { handleCSVExport } = useLocationCsvExport();
  const [hideSocials, setHideSocials] = useState(false);

  return (
    <ModalEcho
      hashProp="location-details"
      title="Location details"
      showDateRangeFilter
      onOpen={trackAudienceLocationModalOpened}
      downloadConfig={{
        onDownload: (...args) => {
          trackAudienceLocationModalDownloadClicked();
          return handleCSVExport(...args);
        },
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: DownloadOutlined as DownloadConfig['downloadButtonIcon'],
      }}
      renderTrigger={(openModal) => (
        <Flex justify="flex-end" style={{ alignSelf: 'flex-end' }}>
          <Button
            type="text"
            color="primary"
            variant="text"
            icon={<RightOutlined />}
            iconPlacement="end"
            onClick={openModal}
          >
            All countries
          </Button>
        </Flex>
      )}
    >
      {(dateRange, selectedShow) => (
        <LocationModalContent
          dateRange={dateRange}
          selectedShow={selectedShow}
          hideSocials={hideSocials}
          onHideSocialsChange={setHideSocials}
        />
      )}
    </ModalEcho>
  );
};

export default LocationModalEcho;
